import { useEffect, useState } from "react";
import { Alert, AlertTitle, Box, ButtonBase } from "@mui/material";
import { motion } from "motion/react";
import { useTranslation } from "next-i18next";
import { IconClose } from "src/components/icons-mui/close";
import { IToastEntry, toastStore } from "src/stores/toast-store";
import { AlertSeverityIcon } from "src/theme-mui/components/alert/alert-severity-icon";

const toastMotionVariants = {
    initial: { opacity: 0, y: -24 },
    animate: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] } },
    exit: { opacity: 0, scale: 0.85, transition: { duration: 0.2, ease: [0.4, 0, 1, 1] } },
};

const TOAST_BUTTON_BASE_SX = {
    borderRadius: 1,
    transition: "all 250ms",
};

interface IToastActionButtonProps {
    buttonText: string;
    onClick: () => void;
}

const ToastActionButton = ({ buttonText, onClick }: IToastActionButtonProps) => (
    <ButtonBase
        onClick={onClick}
        sx={{
            ...TOAST_BUTTON_BASE_SX,
            width: "100%",
            height: "32px",
            minWidth: "32px",
            px: 2,
            fontSize: "sm",
            fontWeight: "semibold",
            color: "gray.800",
            backgroundColor: "common.white",
            "&:hover, &:focus-visible": {
                backgroundColor: "var(--mui-palette-whiteAlpha-800)",
            },
            "&:active": {
                backgroundColor: "var(--mui-palette-whiteAlpha-700)",
            },
        }}
    >
        {buttonText}
    </ButtonBase>
);

interface IToastCloseButtonProps {
    ariaLabel: string;
    onClick: () => void;
}

const ToastCloseButton = ({ ariaLabel, onClick }: IToastCloseButtonProps) => (
    <ButtonBase
        aria-label={ariaLabel}
        onClick={onClick}
        sx={{
            ...TOAST_BUTTON_BASE_SX,
            width: "32px",
            height: "32px",
            color: "common.white",
            backgroundColor: "transparent",
            mt: -0.5,
            mb: -0.5,
            mr: -0.5,
            "&:hover, &:focus-visible": {
                backgroundColor: "var(--mui-palette-whiteAlpha-400)",
            },
            "&:active": {
                backgroundColor: "var(--mui-palette-whiteAlpha-600)",
            },
        }}
    >
        <IconClose sx={{ fontSize: "md" }} />
    </ButtonBase>
);

interface IToastItemProps {
    toast: IToastEntry;
}

export const ToastItem = ({ toast }: IToastItemProps) => {
    const { t } = useTranslation("common");
    const { id, status, title, buttonText, duration, onCloseCallback } = toast;

    const [delay, setDelay] = useState<number | null>(duration);

    useEffect(() => {
        if (delay === null) {
            return;
        }

        const timer = setTimeout(() => toastStore.remove(id), delay);

        return () => clearTimeout(timer);
    }, [id, delay]);

    const handleClose = () => {
        toastStore.remove(id);
        onCloseCallback?.();
    };

    return (
        <motion.div
            layout
            variants={toastMotionVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            onHoverStart={() => setDelay(null)}
            onHoverEnd={() => setDelay(duration)}
            style={{ minWidth: "300px", maxWidth: "560px", margin: "8px", pointerEvents: "auto" }}
        >
            <Alert
                severity={status}
                icon={false}
                sx={{
                    flexDirection: buttonText ? { xs: "column", sm: "row" } : "row",
                    alignItems: buttonText ? { xs: "stretch", sm: "center" } : "center",
                    justifyContent: "space-between",
                    "& .MuiAlert-message": {
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: { xs: "center", sm: "flex-start" },
                        gap: 1,
                        flexGrow: 0,
                        marginRight: { xs: 0, sm: "16px" },
                    },

                    "& .MuiAlert-action": {
                        alignSelf: buttonText ? { xs: "stretch", sm: "center" } : "center",
                        width: buttonText ? { xs: "100%", sm: "auto" } : "auto",
                        margin: 0,
                        padding: 0,
                        marginTop: buttonText ? { xs: "11px", sm: 0 } : 0,
                    },
                }}
                action={
                    buttonText ? (
                        <ToastActionButton buttonText={buttonText} onClick={handleClose} />
                    ) : (
                        <ToastCloseButton ariaLabel={t("close")} onClick={handleClose} />
                    )
                }
            >
                <Box sx={{ display: "flex", flexShrink: 0, fontSize: "24px" }}>
                    <AlertSeverityIcon status={status} />
                </Box>
                <AlertTitle sx={{ margin: 0 }}>{title}</AlertTitle>
            </Alert>
        </motion.div>
    );
};
